import {
  FlexBox,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { Satisfaction } from 'lib-shared-legacy-types';
import noop from 'lodash/noop';
import { useCallback, useEffect, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';

import { ratingApi } from 'apis/rating';
import { SurveySelect } from 'app/views/layout/views/settings/views/satisfaction/survey/components/select/surveySelect';
import { HookFormSelectionListSingle, HookFormText } from 'components/hookForm';
import { createAppModal } from 'utils/modal';

import styles from './selectSurveyModal.module.scss';

export type FieldValues = {
  selectedSurveyType: Satisfaction.SurveyType;
  senderName?: string;
  selectedSurvey: Satisfaction.CsatSettings;
};

/** A component that displays a add survey modal. */
export const SelectSurveyModal = createAppModal(
  { type: 'settings:selectSurvey' },
  ({ isOpen, modal, meta, hide }): JSX.Element => {
    const { currentSurvey, contactPoint, contactPointKind } = modal.arg;

    const [isLoading, setIsLoading] = useState(false);

    const {
      formState,
      control,
      handleSubmit,
      resetField,
      getValues,
      setValue,
    } = useForm<FieldValues>({
      defaultValues: {
        selectedSurveyType:
          currentSurvey?.survey_type ?? Satisfaction.surveyStyles[0],
        senderName: currentSurvey?.additional_settings?.sender_name ?? '',
        selectedSurvey: {
          id: currentSurvey?.rating_settings_id,
          name: currentSurvey?.name,
          organization_id: currentSurvey?.organization_id,
          survey_type: currentSurvey?.survey_type,
          level: 'survey',
          enabled: true,
          customization: {},
        },
      },
      mode: 'all',
    });

    const selectedSurveyType = useWatch({
      control,
      name: 'selectedSurveyType',
    });

    useEffect(() => {
      if (
        currentSurvey?.rating_settings_id &&
        getValues('selectedSurveyType') !== currentSurvey?.survey_type
      ) {
        setValue('selectedSurvey', {} as Satisfaction.CsatSettings);
      } else {
        resetField('selectedSurvey');
      }
    }, [selectedSurveyType]);

    const [linkRatingSettings] = ratingApi.useLazyLinkRatingSettingsQuery();

    const [unlinkRatingSettings] = ratingApi.useLazyUnlinkRatingSettingsQuery();

    /**
     * At the moment only link survey type supports additional data. For other
     * cases it should be undefined, otherwise it could generate conflicts.
     */
    const handleLinkRatingSettingsContactPoint = useCallback(
      async (fieldValues: FieldValues) => {
        const unlinkResponse = await unlinkRatingSettings({
          rating_settings_id: fieldValues.selectedSurvey.id,
          contact_point: contactPoint,
          contact_point_kind: contactPointKind,
        }).unwrap();

        if (isResponseWithData(unlinkResponse)) {
          const linkedResponse = await linkRatingSettings({
            rating_settings_id: fieldValues.selectedSurvey.id,
            contact_point: contactPoint,
            contact_point_kind: contactPointKind,
            additional_settings:
              fieldValues.senderName &&
              fieldValues.selectedSurvey.survey_type == 'link'
                ? { sender_name: fieldValues.senderName }
                : undefined,
          }).unwrap();

          if (isResponseWithData(linkedResponse)) {
            meta?.resolve({ updatedLink: linkedResponse.data });
            hide?.();
          }
        }
      },
      [
        contactPoint,
        contactPointKind,
        hide,
        linkRatingSettings,
        meta,
        unlinkRatingSettings,
      ],
    );

    const handleValidSubmit: SubmitHandler<FieldValues> = async (
      fieldValues,
    ) => {
      setIsLoading(true);
      await handleLinkRatingSettingsContactPoint(fieldValues);
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <form
          noValidate
          tabIndex={-1}
          onSubmit={handleSubmit(handleValidSubmit)}
        >
          <ModalHead
            title={
              <FlexBox gap="200">
                <Text.Heading>Connect a survey to {contactPoint}</Text.Heading>
              </FlexBox>
            }
            onClose={hide}
          />

          <ModalBody>
            <FormGrid>
              <FormField>
                <HookFormSelectionListSingle
                  name="selectedSurveyType"
                  label="Survey send method"
                  control={control}
                  componentProps={{
                    getItemTitleByValue: (
                      value: Satisfaction.SurveyType | null | undefined,
                    ) => (value ? Satisfaction.prettySurveyType(value) : ''),
                  }}
                >
                  {Satisfaction.surveyStyles.map((surveyType) => (
                    <SelectionList.Item key={surveyType} value={surveyType}>
                      <Text.Paragraph>
                        {Satisfaction.prettySurveyType(surveyType)}
                      </Text.Paragraph>
                    </SelectionList.Item>
                  ))}
                </HookFormSelectionListSingle>

                {selectedSurveyType === 'link' && (
                  <FormField>
                    <HookFormText
                      name="senderName"
                      label="Sender Name"
                      control={control}
                      helpIcon={{
                        text: (
                          <Text.Paragraph variant="2" color="white">
                            The text message will display your Sender Name / ID
                            to the customer instead of the phone number.
                            <br />
                            <br />
                            Not available in some countries.
                            <br />
                          </Text.Paragraph>
                        ),

                        dark: true,
                      }}
                      componentProps={{
                        placeholder: 'Sender Name',
                      }}
                      rules={{
                        required: 'Sender Name is required',
                        maxLength: {
                          value: 11,
                          message:
                            'Sender Name can contain up to 11 characters',
                        },
                        pattern: {
                          value: /^(?=.*[A-Za-z])[A-Za-z0-9 ]+$/,
                          message:
                            'Sender Name can contain only letters, digits, space and at least one letter',
                        },
                      }}
                    ></HookFormText>
                  </FormField>
                )}
              </FormField>

              <FormField>
                <SurveySelect
                  control={control}
                  surveyType={selectedSurveyType}
                ></SurveySelect>
              </FormField>
            </FormGrid>
          </ModalBody>

          <ModalFoot
            cancelOnClick={hide}
            confirmName="Add"
            confirmOnClick={noop} // A hack to show confirm button
            confirmProps={{
              disabled: !(formState.isValid && formState.isDirty),
              type: 'submit',
              isLoading,
            }}
          />
        </form>
      </Modal>
    );
  },
);
